import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Panel from '../../../../components/angle/Panel/index';
import { ELECTRONIC_FILING_STATUS } from '../../../../services/constant/constant';
import {
    parseElectronicFiling,
    clearElectronicFilingParse,
    submitNewElectronicFiling,
    fetchElectronicFilings
} from '../../../../services/redux/employee/filing/actions';

const dateFormatter = new Intl.DateTimeFormat('en-US');

class SubmitElectronicFilingPanel extends Component {
    constructor(props) {
        super(props);
        this.state = {
            xml: '',
            submitError: '',
            submitMessage: ''
        };
        this.fileInputRef = React.createRef();
    }

    componentWillUnmount() {
        this.props.clearElectronicFilingParse();
    }

    handleXmlChange = event => {
        this.setState({ xml: event.target.value, submitError: '', submitMessage: '' });
        this.props.clearElectronicFilingParse();
    };

    handleFileChange = event => {
        let file = event.target.files[0];
        if (!file) {
            return;
        }
        let reader = new FileReader();
        reader.onload = e => {
            this.setState({
                xml: e.target.result,
                submitError: '',
                submitMessage: ''
            });
            this.props.clearElectronicFilingParse();
        };
        reader.readAsText(file);
    };

    handleParseClick = () => {
        let { fundSelection } = this.props;
        // Clear any error/success message left over from a previous submit.
        this.setState({ submitError: '', submitMessage: '' });
        this.props.parseElectronicFiling({
            xml: this.state.xml,
            stateId: fundSelection.stateId || 0,
            clientId: fundSelection.clientId || 0,
            trustId: fundSelection.trustId || 0,
            portfolioId: fundSelection.portfolioId || 0,
            classId: fundSelection.classId || 0,
            prospectusId: fundSelection.prospectusId || 0
        }).then(result => {
            // The parse stamps the submit-time message/payment values into the
            // XML and returns it - show that in the textarea so what the user
            // sees is exactly what gets submitted and saved.
            if (result && result.xml) {
                this.setState({ xml: result.xml });
            }
        });
    };

    handleSubmitClick = doFiling => {
        let { fundSelection, submitNewElectronicFiling, fetchElectronicFilings } = this.props;
        let { xml } = this.state;

        submitNewElectronicFiling({
            xml,
            doFiling,
            stateId: fundSelection.stateId || 0,
            clientId: fundSelection.clientId || 0,
            trustId: fundSelection.trustId || 0,
            portfolioId: fundSelection.portfolioId || 0,
            classId: fundSelection.classId || 0,
            prospectusId: fundSelection.prospectusId || 0
        }).then(result => {
            // HTTP-level failure: the controller rejected the request outright
            // (400 for a bad/duplicate XML, 409 for a conflict) or a
            // network/timeout error occurred. The action's catch returns the
            // axios error, which has no top-level status === 200.
            if (!result || result.status !== 200) {
                this.setState({
                    submitError: this.extractErrorMessage(result),
                    submitMessage: ''
                });
                return;
            }

            let filingResult = result.data;

            // The request reached the controller and a Filing row was created,
            // but the live POST to the state's e-filing endpoint failed.
            // SubmitFiling swallows that exception, marks the row Failed, and
            // stashes the state's response / error text in responseBody - yet
            // still returns HTTP 200, so surface it as an error here.
            if (
                doFiling &&
                filingResult.electronicFilingStatus ===
                    ELECTRONIC_FILING_STATUS.FAILED
            ) {
                this.setState({
                    submitError:
                        'Electronic filing failed: ' +
                        (filingResult.responseBody ||
                            'No response received from the state.'),
                    submitMessage: ''
                });
                this.props.clearElectronicFilingParse();
                fetchElectronicFilings();
                return;
            }

            this.setState({
                xml: '',
                submitError: '',
                submitMessage: doFiling
                    ? 'Filing submitted. Confirmation id: ' +
                      (filingResult.confirmationId || '(none returned)')
                    : 'Filing saved.'
            });
            if (this.fileInputRef.current) {
                this.fileInputRef.current.value = '';
            }
            this.props.clearElectronicFilingParse();
            fetchElectronicFilings();
        });
    };

    extractErrorMessage = result => {
        if (result && result.response && result.response.data) {
            let data = result.response.data;
            if (typeof data === 'string' && data) {
                return data;
            }
            if (data.message || data.errorMessage) {
                return data.message || data.errorMessage;
            }
        }
        if (result && result.message) {
            return result.message;
        }
        return 'Submit failed.';
    };

    render() {
        let { filing, fundSelection } = this.props;
        let { xml, submitError, submitMessage } = this.state;
        let parseResult = filing.electronicFilingParse;
        let hasFundSelected = !!(
            fundSelection.trustId ||
            fundSelection.portfolioId ||
            fundSelection.classId ||
            fundSelection.prospectusId
        );
        let hasStateSelected = !!fundSelection.stateId;
        let canSubmit =
            hasStateSelected &&
            hasFundSelected &&
            parseResult &&
            parseResult.errors &&
            parseResult.errors.length === 0;

        return (
            <Panel showHeader header="Submit Electronic Filing">
                <div className="row">
                    <div className="form-group col-md-12">
                        <label>Filing XML</label>
                        <textarea
                            className="form-control"
                            rows={10}
                            value={xml}
                            onChange={this.handleXmlChange}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-12">
                        <input
                            type="file"
                            accept=".xml"
                            ref={this.fileInputRef}
                            onChange={this.handleFileChange}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-12">
                        <button
                            type="button"
                            className="btn btn-default btn-bordered"
                            onClick={this.handleParseClick}
                            disabled={!xml}
                        >
                            Parse
                        </button>
                    </div>
                </div>

                {parseResult && (
                    <div className="row">
                        <div className="col-md-12">
                            {parseResult.errors && parseResult.errors.length > 0 ? (
                                <div className="alert alert-danger">
                                    {parseResult.errors.map((message, index) => (
                                        <div key={index}>{message}</div>
                                    ))}
                                </div>
                            ) : (
                                <table className="table table-condensed">
                                    <tbody>
                                        <tr>
                                            <td>State</td>
                                            <td>{parseResult.stateName}</td>
                                        </tr>
                                        <tr>
                                            <td>Effective Date</td>
                                            <td>
                                                {dateFormatter.format(
                                                    new Date(parseResult.effectiveDate)
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Expiration Date</td>
                                            <td>
                                                {dateFormatter.format(
                                                    new Date(parseResult.expirationDate)
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>State Fee</td>
                                            <td>{parseResult.stateFee}</td>
                                        </tr>
                                        <tr>
                                            <td>Other Fee</td>
                                            <td>{parseResult.otherFee}</td>
                                        </tr>
                                        <tr>
                                            <td>Sales Report</td>
                                            <td>{parseResult.isSalesReport ? 'Yes' : 'No'}</td>
                                        </tr>
                                        <tr>
                                            <td>Termination</td>
                                            <td>{parseResult.isTermination ? 'Yes' : 'No'}</td>
                                        </tr>
                                        <tr>
                                            <td>Issuer (Trust / Portfolio / Class)</td>
                                            <td>
                                                {parseResult.issuerTrustName} /{' '}
                                                {parseResult.issuerPortfolioName} /{' '}
                                                {parseResult.issuerClassName}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}

                {!hasStateSelected && (
                    <div className="alert alert-warning">
                        Choose a State above before submitting.
                    </div>
                )}
                {!hasFundSelected && (
                    <div className="alert alert-warning">
                        Choose a Trust, Portfolio, Class, or Prospectus above before
                        submitting.
                    </div>
                )}
                {submitError && <div className="alert alert-danger">{submitError}</div>}
                {submitMessage && (
                    <div className="alert alert-success">{submitMessage}</div>
                )}

                <div className="row">
                    <div className="form-group col-md-12">
                        <button
                            type="button"
                            className="btn btn-default btn-bordered"
                            onClick={() => this.handleSubmitClick(false)}
                            disabled={!canSubmit}
                        >
                            Save Only
                        </button>{' '}
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => this.handleSubmitClick(true)}
                            disabled={!canSubmit}
                        >
                            Submit to State
                        </button>
                    </div>
                </div>
            </Panel>
        );
    }
}

SubmitElectronicFilingPanel.propTypes = {
    fundSelection: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
    filing: state.employee.filing
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            parseElectronicFiling,
            clearElectronicFilingParse,
            submitNewElectronicFiling,
            fetchElectronicFilings
        },
        dispatch
    );

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(SubmitElectronicFilingPanel);



// WEBPACK FOOTER //
// ./src/screens/EmployeePanel/screens/ElectronicFilings/SubmitElectronicFilingPanel.js